import { navigate } from 'gatsby';
import { equals, find, has, isEmpty, mergeRight, or } from 'ramda';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useAsync, useDeepCompareEffect } from 'react-use';
import { ThemeProvider } from 'styled-components';

import FlowFooter from '@ui/atoms/FlowFooter';
import FlowPageContainer from '@ui/atoms/FlowPageContainer';
import MembershipContainer from '@ui/atoms/MembershipContainer';

import NewFlowHeader from '@ui/organisms/NewFlowHeader';

import useMemberInfo, { IFragment } from '@hooks/useMemberInfo';
import useOnboardedRoute from '@hooks/useOnboardedRoute';
import { useServiceEligibility } from '@hooks/useServiceEligibility';
import useSubscriptionInfo, { OldSubscriptionInfo } from '@hooks/useSubscriptionInfo';
import { useTracking } from '@hooks/useTracking';

import { getCompletedHrtIntakes } from '@helpers/Intakes/intakes';

import {
	getFinalStepIndex,
	getFirstPostOnboardingStep,
	getFirstStep,
	getNextStepForKey,
	getPathForCurrentStep,
	getPathPrefixFromPathname,
	getPreviousStepForKey,
	getStepForKey,
	getStepForPath,
	getStepIndex,
	getStepsForFlow,
} from '@utils/flowHelpers';
import { doesNotExist, exists } from '@utils/general';
import { getAllStateServiceAvailability } from '@utils/service-availability';

import { FlowContextProvider } from '@providers/FlowContextProvider';

import { FolxMembershipStatus } from '@data/membership';

import { FlowIdTypes, FormData } from '@folx-types/flow';
import { FlowStepData } from '@folx-types/flow-steps';
import { IAuthenticatedMemberWithCompletedIntakes } from '@folx-types/member';

import { intakes } from '@graphql/fragments/intakes';

import { getThemeForFlow } from '@theme/FlowThemes';

interface IMemberEstrogenRouteProps {
	path: string;
	children?: ReactNode;
}

const MemberEstrogenRoute: FC<IMemberEstrogenRouteProps> = ({
	children: nestedRouter,
	...props
}) => {
	const {
		props: { children: routes },
	} = nestedRouter;
	const [loaded, setLoaded] = useState(false);
	const [returnKey, setReturnKey] = useState('');
	const [isPaymentWaived, setIsPaymentWaived] = useState(false);

	const parentRef = useRef<HTMLDivElement>(null);

	const FLOW_ID: FlowIdTypes = 'hrt_e';
	const FLOW_PATH = 'hrt_e_sub';

	const currentPath = getPathForCurrentStep(props['*']);
	const stepsForFlow = getStepsForFlow(routes);
	const firstStep = getFirstStep(stepsForFlow) ?? ({} as FlowStepData);
	const postOnboardingStep = getFirstPostOnboardingStep(stepsForFlow);
	const currentStep = getStepForPath(currentPath, stepsForFlow) ?? ({} as FlowStepData);
	const currentIndex = getStepIndex(currentStep, stepsForFlow);
	const finalIndex = getFinalStepIndex(stepsForFlow);
	const isSecureRoute = !!currentStep?.secure;

	const {
		loaded: formLoaded,
		loggedIn,
		formData,
		submitFormData,
		location,
	} = useOnboardedRoute({
		flowId: FLOW_ID,
		flowPath: FLOW_PATH,
		firstStepPath: firstStep.path,
		currentStepKey: currentStep?.stepKey,
		isOnboardingStep: doesNotExist(currentStep) || currentStep?.onboardingStep,
		isSecureRoute,
		onboardedFlowName: 'membership',
		customReroute: '../check_membership',
		customReturn: `../${postOnboardingStep?.path}`,
	});

	const { trackEvent } = useTracking();
	useEffect(() => {
		trackEvent('view-onboarding-step', {
			step_name: currentStep?.stepKey,
			flow_id: FLOW_ID,
		});
	}, [currentStep]);

	const { loading: subscriptionLoading, allSubscriptions, membership } = useSubscriptionInfo();

	const { value: stateServiceMapping, loading: stateLoading } = useAsync(
		async () => await getAllStateServiceAvailability()
	);

	const intakeFragment: IFragment = { name: 'Intakes', node: intakes };
	const { loading: memberLoading, memberInfo } =
		useMemberInfo<IAuthenticatedMemberWithCompletedIntakes>([intakeFragment]);

	const { eligible } = useServiceEligibility(loggedIn);

	useEffect(() => {
		if (!eligible) {
			navigate('/app/flows/hrt_e_sub/state_restriction/');
		}
	}, [eligible]);

	useEffect(() => {
		setLoaded(formLoaded && !subscriptionLoading && !memberLoading && !stateLoading);
	}, [formLoaded, subscriptionLoading, memberLoading, stateLoading]);

	useDeepCompareEffect(() => {
		if (!loggedIn || !loaded || doesNotExist(stateServiceMapping)) {
			return;
		}

		const completedIntakes = getCompletedHrtIntakes(memberInfo);
		if (exists(completedIntakes) && !location.state?.cogsworthId) {
			return navigate('/app/flows/followup');
		}

		const stateServices = (stateServiceMapping ?? {})[
			memberInfo?.stateOfResidence?.abbreviation ?? ''
		];
		const serviceInState = has(FLOW_ID, stateServices);

		const pathPrefix = getPathPrefixFromPathname(location.pathname, FLOW_PATH);
		const newLocation = `/${pathPrefix}/${FLOW_PATH}/ineligible_state`;

		if (!serviceInState) {
			navigate(newLocation);
		}
	}, [stateServiceMapping, memberLoading, loaded, loggedIn]);

	useAsync(async () => {
		if (!loggedIn || !loaded) return;

		const activeMembership =
			(membership?.membershipSubscription?.status, FolxMembershipStatus.ACTIVE);
		if (!activeMembership && !currentStep?.onboardingStep && exists(currentStep)) {
			await navigate('../check_membership', {
				state: { onReturn: `../${postOnboardingStep?.path}` },
			});
		}

		const activeSubscription = or(
			!!find<OldSubscriptionInfo>((s) => s.subscriptionType === 'HRT_E', allSubscriptions ?? []),
			!!find<OldSubscriptionInfo>((s) => s.subscriptionType === 'HRT_T', allSubscriptions ?? [])
		);
		if (activeSubscription) {
			const pathPrefix = getPathPrefixFromPathname(location.pathname, FLOW_PATH);
			const newLocation = `/${pathPrefix}/${FLOW_PATH}/subscribed`;

			if (location.pathname !== newLocation) {
				navigate(newLocation);
			}
		}
	}, [loggedIn, loaded, allSubscriptions, membership]);

	const handleFormSubmission = async (data: FormData, stepKey: string) => {
		if (loggedIn && !isEmpty(data)) {
			await submitFormData(data);
		}
		const combinedData = mergeRight(formData, data);
		const pathPrefix = getPathPrefixFromPathname(location.pathname, FLOW_PATH);

		const nextStep = getNextStepForKey(
			stepsForFlow,
			FLOW_ID,
			currentStep?.stepKey,
			combinedData,
			isPaymentWaived
		);

		const path = getPathForCurrentStep(nextStep?.path ?? '');

		if (returnKey) {
			const returnStep = getStepForKey(stepsForFlow, returnKey);
			setReturnKey('');
			return navigate(`/${pathPrefix}/${FLOW_PATH}/${returnStep?.path}`);
		}

		navigate(`/${pathPrefix}/${FLOW_PATH}/${path}`);
	};

	const handleNavBack = (stepKey: string) => {
		const previousStep = getPreviousStepForKey(stepsForFlow, stepKey);
		const pathPrefix = getPathPrefixFromPathname(location.pathname, FLOW_PATH);
		navigate(`/${pathPrefix}/${FLOW_PATH}/${previousStep.path}`, {
			state: { reverse: true },
		});
	};

	const handleNavNext = (currentStepKey: string) => {
		const nextStep = getNextStepForKey(stepsForFlow, FLOW_ID, currentStepKey, formData);
		const pathPrefix = getPathPrefixFromPathname(location.pathname, FLOW_PATH);
		navigate(`/${pathPrefix}/${FLOW_PATH}/${nextStep.path}`, {
			state: { reverse: false },
		});
	};

	const handleTemporaryNavigation = (stepKey: string, returnKey: string) => {
		setReturnKey(returnKey);
		const pathPrefix = getPathPrefixFromPathname(location.pathname, FLOW_PATH);
		const step = getStepForKey(stepsForFlow, stepKey);
		return navigate(`/${pathPrefix}/${FLOW_PATH}/${step?.path}`);
	};

	const showGradient = equals('INTAKE_INTRODUCTION', currentStep?.stepKey);

	return (
		loaded && (
			<FlowContextProvider
				value={{
					destinationFlow: FLOW_PATH,
					existingFormData: formData,
					flow: FLOW_PATH,
					flowId: FLOW_ID,
					handleFormSubmission,
					handleNavBack,
					handleNavNext,
					handleTemporaryNavigation,
					submitFormData,
					hideNavBack: exists(returnKey),
					parentRef,
					isPaymentWaived,
					setIsPaymentWaived,
				}}>
				<ThemeProvider theme={getThemeForFlow('hrt_e')}>
					<FlowPageContainer
						showGradient={showGradient}
						ref={parentRef}
						header={
							<NewFlowHeader flowName="Estrogen" totalSteps={finalIndex} step={currentIndex} />
						}
						footer={<FlowFooter />}>
						{loaded ? <MembershipContainer>{nestedRouter}</MembershipContainer> : <div />}
					</FlowPageContainer>
				</ThemeProvider>
			</FlowContextProvider>
		)
	);
};

export default MemberEstrogenRoute;
